<template>
  <div data-component="base-settings-page">
    <div
      v-if="title || showSearch || showAddButton"
      class="header"
    >
      <div
        v-if="title"
        class="title"
      >
        <span
          class="text"
          data-text="settings-page-title"
        >
          {{ title }}
        </span>

        <slot name="title-append" />
      </div>

      <transition name="header-actions">
        <div
          v-if="showSearch || showAddButton"
          class="header-actions"
          :class="{ 'is-saving': isUpdating }"
        >
          <or-search
            v-if="showSearch"
            data-input="search"
            :model-value="searchInputValue"
            :placeholder="searchPlaceholderText"
            :disabled="isInitializing || isUpdating || isDisabled"
            @search="$emit('search', $event || '')"
          />

          <or-button
            v-if="showAddButton"
            data-button="add-item"
            :disabled="isInitializing || isUpdating || isDisabled"
            @click="$emit('add-button-click')"
          >
            <or-icon icon="add" />
            {{ $t('HITL.modules.common-settings.create-new-label') }}
          </or-button>
        </div>
      </transition>
    </div>

    <div class="pre-content">
      <slot name="pre-content" />
    </div>

    <div
      class="content"
      :class="{ 'is-loading': isInitializing || isSearching }"
    >
      <or-loader v-if="isInitializing || isSearching" />

      <slot
        v-if="!isInitializing && !isSearching"
        name="content"
      />
    </div>

    <transition
      name="sidebar-panel-appear"
      appear
    >
      <div
        v-if="showSidebar"
        class="sidebar-panel"
        data-component="sidebar"
      >
        <slot name="sidebar" />
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';
import { TranslateResult } from 'vue-i18n';
import {
  OrLoaderV3 as OrLoader,
  OrSearchV3 as OrSearch,
  OrButtonV3 as OrButton,
  OrIconV3 as OrIcon,
} from '@onereach/ui-components-25';

/** TODO: Add more features like search bar or column picker */
export default defineComponent({
  name: 'BaseSettingsPage',
  components: { OrLoader, OrSearch, OrButton, OrIcon },
  props: {
    title: {
      type: String as PropType<string | TranslateResult>,
      default: '',
    },
    searchInputValue: {
      type: String,
      default: '',
    },
    searchPlaceholder: {
      type: String,
      default: '',
    },
    showSidebar: Boolean,
    showAddButton: Boolean,
    showSearch: Boolean,
    isInitializing: Boolean,
    isSearching: Boolean,
    isUpdating: Boolean,
    isDisabled: Boolean,
  },
  emits: ['add-button-click', 'search'],
  computed: {
    searchPlaceholderText() {
      return this.searchPlaceholder || this.$t('HITL.common.search.search');
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='base-settings-page'] {
  --base-settings-page-sidebar-z-index: 10;

  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  height: 100%;
  padding: var(--s-6) var(--s-6) 0;
  background: var(--or-c-surface-1);
  overflow: hidden;

  .header {
    display: flex;
    flex: 0 0 2.5rem;
    align-items: center;
    gap: var(--s-4);

    .title {
      display: flex;
      overflow: hidden;

      .text {
        @include text-overflow;
        font-size: 1.25rem;
        font-weight: var(--fw-2);
        line-height: 1.5;
        color: var(--or-c-on-background);
      }
    }

    &-actions {
      display: flex;
      gap: var(--s-5);
      margin-left: auto;
      overflow: hidden;

      &-enter-active,
      &-leave-active {
        transition: opacity var(--t-duration);
      }

      &-enter,
      &-leave-to {
        opacity: 0;
      }

      .search {
        width: 20rem;
      }
    }
  }

  .content {
    display: flex;
    flex: 1 1 auto;
    margin-bottom: var(--s-6);
    overflow-x: hidden;
    overflow-y: auto;

    &.is-loading {
      align-items: center;
      justify-content: center;
    }
  }

  .sidebar-panel-appear {
    &-enter,
    &-leave-to {
      transform: translateX(100%);
    }

    &-enter-active,
    &-leave-active {
      transition: transform var(--t-duration) var(--t-func);
    }

    &-enter-to,
    &-leave {
      transform: translateX(0);
    }
  }

  .sidebar-panel {
    position: absolute;
    height: 100%;
    max-width: 40rem;
    top: 0;
    right: 0;
    z-index: var(--base-settings-page-sidebar-z-index);
    background: var(--or-c-background);
    box-shadow: var(--or-shadow);
  }
}
</style>
